import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sun, Moon, Globe, Menu, X, User, ShieldCheck, Store } from "lucide-react";
import { useState } from "react";
import { GlobalSearchTrigger } from "@/components/GlobalSearch";
import InstallPWA from "@/components/InstallPWA";

export function TopBar() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const changeLang = (l) => {
    i18n.changeLanguage(l);
    localStorage.setItem("ss_lang", l);
  };

  const navItems = [
    { to: "/", label: t("nav.home") },
    { to: "/directori", label: t("nav.directory") },
    { to: "/incidencies", label: t("nav.incidents") },
    { to: "/targeta-regal", label: t("nav.giftcard") },
    { to: "/consulta-saldo", label: t("nav.check") },
  ];

  return (
    <header className="sticky top-0 z-40 glass border-b border-border/60" data-testid="top-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo-link">
          <img src="/logo.jpg" alt="Shopping Salou" className="w-10 h-10 rounded-xl object-cover shadow-sm" />
          <div className="hidden sm:block">
            <div className="font-heading font-bold text-primary leading-none">Shopping Salou</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Associació de comerciants</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-6">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.replace(/\//g, '') || 'home'}`}
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                loc.pathname === n.to ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user && <div className="hidden md:block"><GlobalSearchTrigger /></div>}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="lang-switcher-btn" className="rounded-full" aria-label={t("a11y.change_lang")}>
                <Globe className="w-4 h-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("common.lang")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[
                { code: "ca", label: "Català" },
                { code: "es", label: "Español" },
                { code: "en", label: "English" },
                { code: "fr", label: "Français" },
              ].map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)} data-testid={`lang-${l.code}`}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost" size="icon" className="rounded-full"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle-btn"
            aria-label={t("a11y.theme_toggle")}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" aria-hidden="true" /> : <Moon className="w-4 h-4" aria-hidden="true" />}
          </Button>

          {user && user.role === "admin" && (
            <Button asChild variant="outline" size="sm" className="rounded-full border-2 hidden sm:inline-flex" data-testid="topbar-admin-btn">
              <Link to="/admin"><ShieldCheck className="w-4 h-4 mr-1.5" aria-hidden="true" /> {t("nav.panel_admin")}</Link>
            </Button>
          )}
          {user && user.role === "associate" && (
            <Button asChild variant="outline" size="sm" className="rounded-full border-2 hidden sm:inline-flex" data-testid="topbar-associate-btn">
              <Link to="/associat"><Store className="w-4 h-4 mr-1.5" aria-hidden="true" /> {t("nav.zone_shop")}</Link>
            </Button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="user-menu-btn" aria-label={t("a11y.user_menu")}>
                  <User className="w-4 h-4 mr-2" aria-hidden="true" />
                  <span className="hidden sm:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin">{t("nav.admin")}</Link></DropdownMenuItem>
                ) : (
                  <DropdownMenuItem asChild><Link to="/associat" data-testid="menu-assoc">{t("nav.zone_assoc")}</Link></DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">{t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="rounded-full" data-testid="nav-login-btn">
              <Link to="/login">{t("nav.login")}</Link>
            </Button>
          )}

          <Button
            variant="ghost" size="icon" className="lg:hidden rounded-full"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? t("a11y.close_menu") : t("a11y.open_menu")}
            aria-expanded={open}
          >
            {open ? <X className="w-4 h-4" aria-hidden="true" /> : <Menu className="w-4 h-4" aria-hidden="true" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border/60 px-4 py-3 space-y-1 glass" data-testid="mobile-menu">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className={`block px-3 py-2 rounded-lg text-sm font-medium ${
                loc.pathname === n.to ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
              data-testid={`mobile-nav-${n.to.replace(/\//g, '') || 'home'}`}
            >
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border/60 mt-24">
      <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <div className="font-heading font-bold text-primary">Shopping Salou</div>
          <div className="text-xs text-muted-foreground">
            {t("footer.copyright")} ·{" "}
            <a href="https://www.shoppingsalou.com" target="_blank" rel="noreferrer" className="hover:text-primary underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-primary rounded" data-testid="footer-web-link">
              www.shoppingsalou.com
            </a>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">{t("footer.tag")}</div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-full focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent"
        data-testid="skip-to-content"
      >
        {t("a11y.skip_to_content")}
      </a>
      <TopBar />
      <main id="main-content" tabIndex="-1" className="flex-1">{children}</main>
      <Footer />
      <InstallPWA />
    </div>
  );
}
